import type { RumEvent, RumResourceEvent } from '@inflearn/monitoring';
import {
  NEXT_PUBLIC_CLARITY_PROJECT_ID,
  NEXT_PUBLIC_DATADOG_APP_ID,
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  NEXT_PUBLIC_DATADOG_ENV,
  NEXT_PUBLIC_DATADOG_SERVICE,
  NEXT_PUBLIC_ENV,
  NEXT_PUBLIC_VERSION,
} from '../../../appConfig';
import { IGNORE_RESOURCE_URLS } from './ignoreErrors';
import { initLogs } from './initLogs';

export async function initMonitoring() {
  if (NEXT_PUBLIC_ENV === 'localhost') {
    return;
  }

  const { isBot, MonitoringService } = await import('@inflearn/monitoring');
  const { ignoreErrors } = await import('./ignoreErrors');

  const monitoringService = MonitoringService.getInstance();

  const getSampleRate = () => {
    if (isBot(window.navigator.userAgent)) {
      return {
        sessionSampleRate: 0,
        sessionReplaySampleRate: 0,
      };
    }

    if (NEXT_PUBLIC_ENV === 'development') {
      return {
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
      };
    }

    return {
      sessionSampleRate: 50,
      sessionReplaySampleRate: 10,
    };
  };

  const { sessionSampleRate, sessionReplaySampleRate } = getSampleRate();

  await monitoringService.initialize({
    datadogConfig: {
      applicationId: NEXT_PUBLIC_DATADOG_APP_ID ?? '',
      clientToken: NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
      service: NEXT_PUBLIC_DATADOG_SERVICE,
      env: NEXT_PUBLIC_DATADOG_ENV,
      version: NEXT_PUBLIC_VERSION,

      // # 샘플링 설정
      sessionSampleRate,
      sessionReplaySampleRate,

      // # 트래킹 설정
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      enableExperimentalFeatures: ['feature_flags'],

      allowedTracingUrls: [new RegExp(/https:\/\/*.(inflearn|devinflearn)\.com/)],
    },
    clarityConfig: {
      projectId: NEXT_PUBLIC_CLARITY_PROJECT_ID ?? '',
      consent: true,
    },
    ignoreErrors,
    customBeforeSend: (event) => {
      if (isRumResourceEvent(event)) {
        return !IGNORE_RESOURCE_URLS.some((url) => event.resource.url.includes(url));
      }

      return true;
    },
  });

  await initLogs();
}

const isRumResourceEvent = (event: RumEvent): event is RumResourceEvent =>
  event.type === 'resource';
